import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorScreen } from './ErrorScreen';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error Boundary global. Captura cualquier error de render (incluido el de los
 * providers, ya que se monta como wrapper más externo) y muestra una pantalla
 * con la marca en lugar del fallo en blanco del navegador.
 *
 * Es un componente de clase porque los Error Boundaries de React solo se pueden
 * implementar así. Para errores de render dentro de las rutas, React Router usa
 * su propio boundary (ver `RouteErrorBoundary`); este cubre el resto del árbol.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log para diagnóstico; aquí podría integrarse un servicio de tracking.
    console.error('Render error captured by AppErrorBoundary:', error, info);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorScreen />;
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
